import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Input type for updating business details (without id, injected from provider)
 */
export interface UpdateBusinessDetailsInput {
	servicesProvider: Partial<ServicesProvider>;
}

/**
 * API function to update business details for a services provider
 */
export const updateBusinessDetails = async (
	servicesProviderId: string,
	input: UpdateBusinessDetailsInput,
): Promise<ServicesProvider> => {
	const response = await apiClient.patch<ServicesProvider>(
		configurationEndpoints.businessDetails(servicesProviderId),
		input,
	);
	return response.data;
};

/**
 * Hook for updating business details for a services provider
 */
export const useUpdateBusinessDetails = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: UpdateBusinessDetailsInput) => {
			if (!servicesProviderId) {
				throw new Error('Unable to update business details without a services provider ID.');
			}
			return updateBusinessDetails(servicesProviderId, input);
		},
		onSuccess: (data) => {
			// Update the cache directly with the response data
			queryClient.setQueryData(
				queries.servicesProviders.detail(data.id).queryKey,
				data,
			);
		},
	});
};
